"use client";

import { useMemo, useState } from "react";

import {
  useProfileContacts,
  useAddManualContact,
  useRefreshContact,
  useContactFeedback,
  useDeleteManualContact,
  useRevealContact,
} from "@/hooks/use-contact-reveal";

import { ContactRevealConfirmAction } from "./reveal-confirm-action";
import { ContactReviewAction } from "./review";

export type ContactReviewCandidatePreview = {
  name: string;
  jobTitle?: string | null;
  company?: string | null;
  avatarUrl?: string | null;
};

/**
 * Shared "available contact information" modal — binds the profile-contacts
 * cache (`useProfileContacts`) to the `ContactReviewAction` surface, wires
 * the add / refresh / feedback / delete mutations, and opens the credit
 * breakdown confirm step before reveal/enrich.
 */
export function ContactReviewModal({
  profileId,
  open,
  onOpenChange,
  revealAction,
  candidate,
}: {
  profileId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  revealAction: "reveal" | "enrich";
  candidate?: ContactReviewCandidatePreview | null;
}) {
  const [revealConfirmOpen, setRevealConfirmOpen] = useState(false);
  const { data: contactData } = useProfileContacts(profileId);
  const revealMutation = useRevealContact(profileId);
  const addManualMutation = useAddManualContact(profileId);
  const refreshMutation = useRefreshContact(profileId);
  const feedbackMutation = useContactFeedback(profileId);
  const deleteMutation = useDeleteManualContact(profileId);

  const reviewData = useMemo(() => {
    const allContacts = contactData?.contacts ?? [];
    const revealed = contactData?.already_revealed ?? false;

    return {
      revealed,
      local: {
        emails: allContacts.filter((c) => c.type === "email" && c.isLocal),
        phoneNumbers: allContacts.filter(
          (c) => c.type === "phone" && c.isLocal,
        ),
      },
      remote: {
        personalEmails: allContacts.filter(
          (c) => c.type === "email" && !c.isLocal && c.emailType === "personal",
        ),
        professionalEmails: allContacts.filter(
          (c) =>
            c.type === "email" && !c.isLocal && c.emailType === "professional",
        ),
        phoneNumbers: allContacts.filter(
          (c) => c.type === "phone" && !c.isLocal,
        ),
      },
    };
  }, [contactData]);

  const handleReviewOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) setRevealConfirmOpen(false);
  };

  const handleStartReveal = () => {
    onOpenChange(false);
    setRevealConfirmOpen(true);
  };

  const handleRevealConfirm = async () => {
    await revealMutation.mutateAsync();
    setRevealConfirmOpen(false);
    onOpenChange(true);
  };

  const handleRevealConfirmOpenChange = (next: boolean) => {
    setRevealConfirmOpen(next);
    if (!next && !revealMutation.isPending) {
      onOpenChange(true);
    }
  };

  return (
    <>
      <ContactReviewAction
        open={open}
        onOpenChange={handleReviewOpenChange}
        contactData={reviewData}
        onReveal={handleStartReveal}
        onAddEmail={async (email) => {
          await addManualMutation.mutateAsync({ type: "email", value: email });
        }}
        onAddPhone={async (phone) => {
          await addManualMutation.mutateAsync({ type: "phone", value: phone });
        }}
        onRefresh={async () => {
          await refreshMutation.mutateAsync();
        }}
        refreshing={refreshMutation.isPending}
        onFeedback={(contactId, feedback) =>
          feedbackMutation.mutate({ contactId, feedback })
        }
        onDeleteContact={async (contactId) => {
          await deleteMutation.mutateAsync(contactId);
        }}
        profileId={profileId}
        revealAction={revealAction}
      />

      <ContactRevealConfirmAction
        open={revealConfirmOpen}
        onOpenChange={handleRevealConfirmOpenChange}
        onConfirm={handleRevealConfirm}
        loading={revealMutation.isPending}
        action={revealAction}
        candidate={candidate}
      />
    </>
  );
}
